/**
 * ScanListItem — one row in the scan list beside the "Scans by location" map.
 *
 * Deliberately the SAME row as the certificate page's Scans tab
 * (`CertificatePage/components/Scans/components/ScanItem`): same 44px-ish media +
 * two-line cell on the left, same status chip + relative time on the right, same
 * `data-scan-id` hook, and the same persistent selected treatment (brand tint plus an
 * inset left accent) so a row and its map pin always agree about what is selected.
 *
 * The ONE difference is the identity in the left cell, and it is forced by the data:
 * the certificate page shows the SCANNER (every row is the same certificate, so the
 * person is the variable), while this surface is cross-certificate and
 * `IScanMapPoint` carries no user at all — so the PRODUCT is the variable and the
 * identity. That also matches the map's own hover tooltip, which is built from the
 * product image + title + location by `buildScanTooltip`, so hovering a pin and
 * reading its row show the same thing.
 */
import React from 'react'

import { Box, Divider, Stack, Tooltip, Typography, alpha } from '@mui/material'

import type { IScanMapPoint } from '@repo/types'
import { Image, getScanStatus, type ScanStatusInput } from '@repo/ui'

import { StatusChip } from 'components/display'
// The shared name→glyph map. It is exported from the certificate page's ScanItem
// precisely so it is not re-typed per surface (§36) — `getScanStatus` names the icon
// but cannot import one, so the mapping lives app-side and there is one of it.
import { ICONS } from 'pages/main/CertificatePage/components/Scans/components/ScanItem'

import { datetime, timeAgo } from 'utils'

interface ScanListItemProps {
  point: IScanMapPoint
  isActive?: boolean
  onClick?: (scanId: string) => void
  /**
   * True when this row stands for a CERTIFICATE at its last known location rather than
   * for one scan. The row's shape is unchanged, but its timestamp changes meaning, so
   * the tooltip names which reading applies. Without it "2 hours ago" invites the list
   * to be read as a scan feed when it is one row per certificate.
   */
  perCertificate?: boolean
}

function scanStatusInput(point: IScanMapPoint): ScanStatusInput {
  return {
    invalid: point.invalid,
    tampered: point.tampered,
    metadata: point.metadata as { tampered?: boolean | null } | null,
  }
}

export const ScanListItem = ({ point, isActive, onClick, perCertificate }: ScanListItemProps) => {
  const location = [point.geo_city, point.geo_state, point.geo_country].filter(Boolean).join(', ')

  const status = getScanStatus(scanStatusInput(point))
  const StatusIcon = status.icon ? ICONS[status.icon] : undefined
  /**
   * ── THE INDICATOR IS AN ALERT, NOT A SOLID BADGE (VERA-260) ─────────────────────
   *
   * 🚨 `backgroundColor` and `borderColor` are now passed, and their absence was a real
   * bug rather than a stylistic gap. `statusTone` (`StatusChip`) falls back to
   * `alpha(color, 0.14)` for the fill and to `color` itself for the edge when a status
   * carries neither — so handing it `{label, color}` alone made every scan status render
   * as a SOLID TINTED PILL in its marker hue. For Invalid that meant a filled red badge,
   * the exact opposite of what `getScanStatus` authors: its `chip` ramp is
   * `{ fill: 'transparent', border: '#FECACA', text: '#27272A' }`, and its own comment
   * explains why — "transparent fill + red border, with the red carried by the ICON
   * rather than a filled block. Reads as an alert without competing with the solid red
   * of a Stolen certificate." The authored ramp existed; this call site was discarding it.
   *
   * Opened was wrong in the same way and more visibly: `tampered.color` is the VERIFIED
   * PURPLE (a broken seal is not a failed scan, so its pin stays purple), so the row drew
   * a purple-filled badge where the resolver authors neutral zinc.
   *
   * `color` deliberately stays the MARKER hue rather than the ramp's near-black `text`.
   * It drives the glyph — which is what carries the alert now that the fill does not —
   * and, more importantly, dark mode: `statusTone` derives the whole dark chip from
   * `color` alone, so a near-black `color` renders as grey there. That is the same trap
   * `SCAN_STATUS_OPTIONS` documents for the filter chips, and the same resolution.
   *
   * Net effect: Invalid is a red shield-alert on a transparent field with a pale red
   * edge, matching the map legend's Invalid pill glyph-for-glyph (both resolve
   * `status.icon` through the shared `ICONS` map), which is what "the two surfaces match"
   * asks for.
   */
  const statuses = {
    [status.key]: {
      label: status.label,
      color: status.color,
      backgroundColor: status.chip.fill,
      borderColor: status.chip.border,
      icon: StatusIcon ? <StatusIcon size={13} strokeWidth={2} /> : undefined,
      // The chip explains its own term (§44/§59) — the resolver names which glossary
      // entry defines this state and `StatusChip` does the rest.
      glossaryId: status.glossaryId,
    },
  }

  return (
    <>
      <Stack
        data-scan-id={point.scan_id}
        direction="row"
        spacing={4}
        py={3}
        px={4}
        justifyContent="space-between"
        alignItems="center"
        onClick={() => onClick?.(point.scan_id)}
        sx={{
          cursor: 'pointer',
          transition: 'background-color 0.15s, box-shadow 0.15s',
          backgroundColor: isActive
            ? t => alpha(t.palette.brand?.[500] ?? t.palette.primary.main, 0.14)
            : 'transparent',
          boxShadow: isActive
            ? t => `inset 3px 0 0 ${t.palette.brand?.[500] ?? t.palette.primary.main}`
            : 'none',
          '&:hover': {
            backgroundColor: t =>
              isActive
                ? alpha(t.palette.brand?.[500] ?? t.palette.primary.main, 0.14)
                : t.palette.action.hover,
          },
        }}
      >
        <Stack direction="row" spacing={3} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          {/* `Image` renders its own neutral placeholder when `src` is empty, so a
              product with no image still holds the row's footprint instead of
              collapsing the text left. */}
          <Image
            src={point.product_image || ''}
            alt={point.product_title}
            fit="cover"
            sx={{ width: 44, height: 44, minWidth: 44, borderRadius: 1.5 }}
          />
          <Box sx={{ minWidth: 0 }}>
            {/* `component="p"` is load-bearing, not cosmetic. MUI's `noWrap` sets
                `overflow: hidden` + `text-overflow: ellipsis`, and NEITHER applies to a
                non-replaced INLINE box — and `body3` has no variantMapping, so
                Typography defaults it to a `<span>`. Measured before this: the title
                rendered 193px wide inside a 79px column and ran straight over the
                status chip beside it. As a block it ellipsizes as intended. */}
            <Typography variant="body3" component="p" noWrap>
              {point.product_title}
            </Typography>
            {/* Blank when the visitor declined the location prompt — the row does not
                claim "hidden" or "unknown", same as the certificate page. */}
            {location && (
              <Typography variant="body4" color="text.secondary" noWrap component="p">
                {location}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack direction="row" spacing={3} alignItems="center" flexShrink={0}>
          {status.showChip && <StatusChip statuses={statuses} status={status.key} size="small" />}
          {/* Per certificate the timestamp is the last LOCATED scan, which is what
              pins the certificate to this place. "Last scanned" is the honest label
              for it: it is the most recent scan we have a location for, not
              necessarily the most recent scan. */}
          <Tooltip
            title={
              perCertificate
                ? `Last scanned here ${datetime(point.created_at)}`
                : datetime(point.created_at)
            }
            placement="left"
            arrow
          >
            <Typography variant="body3" color="text.secondary" noWrap sx={{ cursor: 'default' }}>
              {timeAgo(point.created_at)}
            </Typography>
          </Tooltip>
        </Stack>
      </Stack>
      <Divider />
    </>
  )
}
